import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { API_URL, resolveAssetUrl } from './bookingUtils';
import { SignaturePad } from './SignaturePad';

const DEFAULTS = { company_name: 'Goldsands Group Inc', logo_url: '', primary_color: '#D4AF37', watermark_text: 'Goldsands Group USA', signatory_name: '', signatory_title: '', signatory_signature: '' };

export const BrandingDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drawSig, setDrawSig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API_URL}/api/booking-forms/branding`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : DEFAULTS))
      .then(data => setForm({ ...DEFAULTS, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null)) }))
      .catch(() => setForm(DEFAULTS))
      .finally(() => setLoading(false));
    setDrawSig(false);
  }, [open]);

  const set = (key) => (value) => setForm(prev => ({ ...prev, [key]: value }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API_URL}/api/landing-pages/upload-image?image_type=logo`, { method: 'POST', credentials: 'include', body });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      set('logo_url')(data.url);
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const useLandingPageLogo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/landing-pages/global-settings`, { credentials: 'include' });
      const data = res.ok ? await res.json() : {};
      if (!data.logo_url) return toast.error('No landing-page logo has been uploaded yet');
      set('logo_url')(data.logo_url);
      toast.success('Landing-page logo applied');
    } catch {
      toast.error('Could not load landing-page settings');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-forms/branding`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, logo_url: form.logo_url || null, signatory_signature: form.signatory_signature || null }),
      });
      if (!res.ok) throw new Error('Failed to save branding');
      toast.success('Branding saved');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const logoSrc = resolveAssetUrl(form.logo_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Form Branding</DialogTitle>
          <DialogDescription>Logo, company name and colours shown on every client-facing booking form and PDF.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input value={form.company_name} onChange={(e) => set('company_name')(e.target.value)} data-testid="branding-company-name" />
              </div>
              <div>
                <Label>Logo</Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="h-16 w-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {logoSrc ? <img src={logoSrc} alt="Logo" className="max-h-14 max-w-[150px] object-contain" data-testid="branding-logo-preview" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  {form.logo_url && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => set('logo_url')('')} data-testid="branding-remove-logo">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                    <label className="cursor-pointer" data-testid="branding-upload-logo-btn">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      Upload logo
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="branding-logo-file-input" />
                    </label>
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={useLandingPageLogo} data-testid="branding-use-lp-logo-btn">
                    Use landing-page logo
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">PNG with transparent background recommended (3:1 ratio).</p>
              </div>
              <div>
                <Label>Primary Colour</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={form.primary_color} onChange={(e) => set('primary_color')(e.target.value)}
                    className="h-10 w-12 rounded border border-slate-300 p-1 bg-white" data-testid="branding-color-picker" />
                  <Input value={form.primary_color} onChange={(e) => set('primary_color')(e.target.value)} className="font-mono" data-testid="branding-color-input" />
                </div>
              </div>
              <div>
                <Label>Footer Text</Label>
                <Input value={form.watermark_text} onChange={(e) => set('watermark_text')(e.target.value)} data-testid="branding-footer-text" />
              </div>
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Authorised Signatory</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={form.signatory_name} onChange={(e) => set('signatory_name')(e.target.value)} data-testid="branding-signatory-name" /></div>
                  <div><Label>Title</Label><Input value={form.signatory_title} onChange={(e) => set('signatory_title')(e.target.value)} placeholder="Director" data-testid="branding-signatory-title" /></div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Signature</Label>
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <label className="cursor-pointer" data-testid="branding-upload-signature-btn">
                        <Upload className="w-3.5 h-3.5 mr-1" /> Upload image
                        <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="branding-signature-file-input" onChange={(e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const reader = new FileReader(); reader.onload = () => { set('signatory_signature')(reader.result); setDrawSig(false); }; reader.readAsDataURL(f);
                        }} />
                      </label>
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => { setDrawSig(true); set('signatory_signature')(''); }} data-testid="branding-draw-signature-btn">Draw</Button>
                  </div>
                </div>
                {drawSig ? (
                  <SignaturePad onChange={(v) => set('signatory_signature')(v || '')} />
                ) : form.signatory_signature ? (
                  <div className="flex items-center gap-3">
                    <img src={form.signatory_signature} alt="Signature" className="h-14 object-contain bg-white border rounded px-2" data-testid="branding-signature-preview" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => set('signatory_signature')('')} data-testid="branding-remove-signature"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No saved signature — you can still draw one per booking.</p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Live preview</Label>
              <div className="mt-1 rounded-xl border border-slate-200 overflow-hidden shadow-inner" data-testid="branding-live-preview">
                <div className="bg-[#0F172A] text-white p-5 border-b-2" style={{ borderColor: form.primary_color }}>
                  <div className="flex items-center gap-3">
                    {logoSrc ? <img src={logoSrc} alt="" className="h-9 object-contain" /> : <div className="h-9 w-9 rounded bg-white/10" />}
                    <div>
                      <p className="font-heading text-lg leading-tight">{form.company_name || 'Company Name'}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Official Booking Form</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#FAFAF9] p-5 space-y-3">
                  <div className="h-3 w-2/3 rounded bg-slate-200" />
                  <div className="h-10 rounded-lg border bg-white" style={{ borderColor: `${form.primary_color}80` }} />
                  <div className="h-9 w-32 rounded-lg" style={{ backgroundColor: form.primary_color }} />
                  <p className="text-[10px] text-slate-400 text-center pt-2">{form.watermark_text}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="branding-save-btn">
            {saving ? 'Saving...' : 'Save Branding'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
